import React, { Fragment, useState, useEffect, useMemo } from "react";
import usePersistentFilters, { usePersistentState } from "../Common/usePersistentFilters";
import { Row, Col, Card, Table, Badge, Modal, Button, Spinner, Form } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router-dom";
import api, { getFileUrl as getApiFileUrl } from "../../../api";
import PageTitle from "../../layouts/PageTitle";
import swal from "sweetalert";
import * as XLSX from "xlsx";
import PermissionWrapper from "../PermissionWrapper";
import EmailModal from "../Common/EmailModal";
import { useSelector } from "react-redux";
import WorkOrderCards from "./WorkOrderCards";
import AppPagination from "../Common/AppPagination";

const WorkOrders = () => {
 const user = useSelector((state) => state.auth?.userDetails || {});

 const normalize = (str) => (str || "").toLowerCase().replace(/[:\s_-]/g, "");
 const userPerms = (user?.permissionNames || []).map((p) => normalize(p));
 const hasBillingPerm =
 userPerms.includes("readbillings") ||
 userPerms.includes("managebillings") ||
 (user?.role?.name || user?.roleName || "").toLowerCase() === "admin";

 const [workOrders, setWorkOrders] = useState([]);
 const [confirmDelete, setConfirmDelete] = useState({ show: false, wo: null });
 const [showEmailModal, setShowEmailModal] = useState(false);
 const [emailInitialData, setEmailInitialData] = useState({});
 const [documentsModal, setDocumentsModal] = useState({
 show: false,
 loading: false,
 workOrder: null,
 documents: [],
 });
 const [deductionTypes, setDeductionTypes] = useState([]);
 const [deductionModal, setDeductionModal] = useState({
 show: false,
 loading: false,
 saving: false,
 workOrder: null,
 originalIds: [],
 rows: [{ _id: "", name: "", percentage: "", remarks: "" }],
 });
 const navigate = useNavigate();
 const location = useLocation();
 const [loading, setLoading] = useState(true);
 const params = new URLSearchParams(location.search);
 const projectId = params.get("project");
 const [viewMode, setViewMode] = useState(() => {
 return localStorage.getItem("workOrderViewMode") || "cards";
 });
  const [billings, setBillings] = useState([]);
  const [filters, setFilters] = usePersistentFilters("work_orders_filters", {
    search: "",
    client: "",
    project: projectId || "",
    status: "In Progress",
    billingStatus: "",
  });
  const [currentPage, setCurrentPage] = usePersistentState("work_orders_page", 1);
  const itemsPerPage = 10;

  useEffect(() => {
    if (viewMode !== "table") return;
    const controller = new AbortController();
    fetchWorkOrders(controller.signal);
    return () => controller.abort();
  }, [projectId, viewMode]);

  const fetchWorkOrders = async (signal) => {
    try {
      setLoading(true);

      const woUrl = projectId
        ? `/work-orders?project=${projectId}&limit=1000`
        : `/work-orders?limit=1000`;

      const [woRes, billingRes] = await Promise.all([
        api.get(woUrl, { signal }),
        hasBillingPerm
          ? api.get("/billing", { signal }).catch(() => ({ data: { data: [] } }))
          : Promise.resolve({ data: { data: [] } }),
      ]);

      const result = woRes.data;

      if (result.success && Array.isArray(result.data)) {
        const mappedOrders = result.data
          .filter((wo) => wo.project && (wo.project._id || wo.project.projectName))
          .map((wo) => ({
            id: wo._id,
            _id: wo._id,
            raw: wo,

            woNo: wo.woNo || "N/A",
            label: wo.label || "",

            client: wo.client?.companyName || wo.client?.name || "N/A",
            clientEmail: wo.client?.gmail || wo.client?.email || "",
            clientId: wo.client?._id ? String(wo.client._id) : "",

            project: wo.project?.projectName || wo.project?.projectCode || "N/A",
            projectId: wo.project?._id ? String(wo.project._id) : "",

            location: wo.project?.location || "-",

            value: Number(wo.totalAmount || wo.value || 0),
            baseValue: Number(wo.value || 0),
            valueText: wo.valueText || "",

            totalDays: wo.totalDays || null,
            status: wo.status || "Pending",
            sacCode: wo.sacCode || "995461",

            startDate: wo.startDate
              ? new Date(wo.startDate).toISOString().split("T")[0]
              : "",

            endDate: wo.endDate
              ? new Date(wo.endDate).toISOString().split("T")[0]
              : "",
          }));

        setWorkOrders(mappedOrders);
        setBillings(billingRes.data?.data || []);
      } else {
        setWorkOrders([]);
        setBillings([]);
      }
    } catch (error) {
      if (error.name === "CanceledError") return;
      setWorkOrders([]);
      setBillings([]);
      console.error("[ERROR] Error fetching work orders:", error);
    } finally {
      setLoading(false);
    }
  };

  /* =============================
     FILTER OPTIONS & HELPERS
  ============================= */
  const clientOptions = useMemo(() => {
    const map = new Map();
    workOrders.forEach((wo) => {
      const client = wo.raw?.client || {};
      const clientId = client._id ? String(client._id) : String(wo.clientId || "");
      if (!clientId) return;
      if (!map.has(clientId)) {
        map.set(clientId, {
          value: clientId,
          label: client.companyName || client.name || wo.client || "Client",
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [workOrders]);

  const projectOptions = useMemo(() => {
    const map = new Map();
    workOrders.forEach((wo) => {
      const proj = wo.raw?.project || {};
      const projId = proj._id ? String(proj._id) : String(wo.projectId || "");
      if (!projId) return;
      if (!map.has(projId)) {
        map.set(projId, {
          value: projId,
          label: proj.projectName || proj.projectCode || wo.project || "Project",
        });
      }
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [workOrders]);

  const getWorkOrderBillingStatus = (wo) => {
    const totalValue = Number(wo.value || 0);
    const woId = String(wo.id || wo._id);
    const billed = billings.reduce((sum, b) => {
      const billingWoId = String(typeof b.workOrder === "object" ? b.workOrder?._id : b.workOrder || "");
      if (billingWoId !== woId) return sum;
      return sum + Number(b.totalAmount || 0);
    }, 0);

    if (billed <= 0) return "Unbilled";
    if (!totalValue) return "Billed";
    if (billed >= totalValue) return "Fully Billed";
    return "Partially Billed";
  };

  const filteredWorkOrders = useMemo(() => {
    const search = String(filters.search || "").trim().toLowerCase();

    return workOrders.filter((wo) => {
      const billingStatus = getWorkOrderBillingStatus(wo);
      const clientId = String(wo.clientId || wo.raw?.client?._id || "");
      const projId = String(wo.projectId || wo.raw?.project?._id || "");
      const woNo = String(wo.woNo || "").toLowerCase();
      const label = String(wo.label || "").toLowerCase();
      const projectName = String(wo.project || "").toLowerCase();
      const projectCode = String(wo.raw?.project?.projectCode || "").toLowerCase();
      const clientName = String(wo.client || "").toLowerCase();

      if (filters.client && clientId !== filters.client) return false;
      if (filters.project && projId !== filters.project) return false;
      if (filters.status === "Completed" && wo.status !== "Completed") return false;
      if (filters.status === "In Progress" && wo.status === "Completed") return false;
      if (filters.billingStatus && billingStatus !== filters.billingStatus) return false;

      if (
        search &&
        !woNo.includes(search) &&
        !label.includes(search) &&
        !projectName.includes(search) &&
        !projectCode.includes(search) &&
        !clientName.includes(search)
      ) {
        return false;
      }

      return true;
    });
  }, [workOrders, filters, billings]);

  const totalPages = Math.ceil(filteredWorkOrders.length / itemsPerPage) || 1;
  const currentTableItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredWorkOrders.slice(start, start + itemsPerPage);
  }, [filteredWorkOrders, currentPage, itemsPerPage]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      client: "",
      project: projectId || "",
      status: "In Progress",
      billingStatus: "",
    });
    setCurrentPage(1);
  };

 const handleView = (wo) =>
 navigate(
 projectId
 ? `/work-orders/details/${wo.id}?project=${projectId}`
 : `/work-orders/details/${wo.id}`
 );
 const handleEdit = (wo) =>
 navigate(
 projectId
 ? `/work-orders/edit/${wo.id}?project=${projectId}`
 : `/work-orders/edit/${wo.id}`
 );
 const handleGantt = (wo) =>
 navigate(
 projectId
 ? `/work-orders/${wo.id}/gantt?project=${projectId}`
 : `/work-orders/${wo.id}/gantt`
 );

 const handleExportSummary = () => {
    const listToExport = filteredWorkOrders.length ? filteredWorkOrders : workOrders;
    if (!listToExport.length) {
 swal("Warning", "No work orders to export", "warning");
 return;
 }

 const exportData = listToExport.map((wo, index) => {
 const row = {
 "#": index + 1,
 "WO No": wo.woNo,
 "SAC Code": wo.sacCode,
 Client: wo.client,
 Project: wo.project,
 Location: wo.location,
 };

 if (hasBillingPerm) {
 row["Value"] = wo.value;
 }

 row["Status"] = wo.status;
 row["Start Date"] = wo.startDate || "-";
 row["End Date"] = wo.endDate || "-";
 row["Duration"] = wo.totalDays ? `${wo.totalDays} days` : "-";

 return row;
 });

 const worksheet = XLSX.utils.json_to_sheet(exportData);

 const cols = [
 { wch: 5 },
 { wch: 15 },
 { wch: 20 },
 { wch: 35 },
 { wch: 20 },
 { wch: 15 },
 ];

 if (hasBillingPerm) {
 cols.push({ wch: 15 });
 }

 cols.push(
 { wch: 15 },
 { wch: 15 },
 { wch: 15 },
 { wch: 15 }
 );

 worksheet["!cols"] = cols;

 const workbook = XLSX.utils.book_new();
 XLSX.utils.book_append_sheet(workbook, worksheet, "Work Orders");

 XLSX.writeFile(
 workbook,
 `WorkOrders-Summary.xlsx`,
 );
 };

 const confirmDeleteWO = async () => {
 if (!confirmDelete.wo) return;

 try {
 const response = await api.delete(`/work-orders/${confirmDelete.wo.id}`);
 const result = response.data;

 if (result.success) {
 setWorkOrders((orders) =>
 orders.filter((wo) => wo.id !== confirmDelete.wo.id),
 );
 swal("Deleted!", "Work order deleted successfully.", "success");
 } else {
 swal(
 "Error",
 result.message || "Failed to delete work order.",
 "error",
 );
 }
 } catch (e) {
 swal(
 "Error",
 e.response?.data?.message || "Error deleting work order.",
 "error",
 );
 } finally {
 setConfirmDelete({ show: false, wo: null });
 }
 };

 const getFileUrl = (fileUrl) => getApiFileUrl(fileUrl);

 const normalizeTypeName = (typeName) =>
 String(typeName || "")
 .toLowerCase()
 .replace(/\s+/g, "");

 const getDeductionRowTypeId = (row) =>
 row.name?._id ||
 row.name ||
 row.deductionType?._id ||
 row.deductionType ||
 row.type?._id ||
 row.type ||
 "";

 const getDeductionRows = () =>
 deductionModal.rows.length
 ? deductionModal.rows
 : [{ _id: "", name: "", percentage: "", remarks: "" }];

 const totalDeductionPercentage = getDeductionRows().reduce(
 (sum, row) => sum + Number(row.percentage || 0),
 0,
 );

 const totalDeductionAmount =
 ((deductionModal.workOrder?.value || 0) * totalDeductionPercentage) / 100;

 const addDeductionRow = () => {
 setDeductionModal((prev) => ({
 ...prev,
 rows: [...getDeductionRows(), { _id: "", name: "", percentage: "", remarks: "" }],
 }));
 };

 const removeDeductionRow = (index) => {
 setDeductionModal((prev) => ({
 ...prev,
 rows:
 prev.rows.length === 1
 ? prev.rows
 : prev.rows.filter((_, rowIndex) => rowIndex !== index),
 }));
 };

 const updateDeductionRow = (index, field, value) => {
 setDeductionModal((prev) => ({
 ...prev,
 rows: prev.rows.map((row, rowIndex) =>
 rowIndex === index ? { ...row, [field]: value } : row,
 ),
 }));
 };

 const handleOpenDeduction = async (wo) => {
 setDeductionModal({
 show: true,
 loading: true,
 saving: false,
 workOrder: wo,
 originalIds: [],
 rows: [{ _id: "", name: "", percentage: "", remarks: "" }],
 });

 try {
 const [typesRes, deductionsRes] = await Promise.all([
 api.get("/deduction-types"),
 api
 .get(`/deductions?workOrder=${wo.id}&limit=1000`)
 .catch(() => ({ data: { data: [] } })),
 ]);

 const activeTypes = (typesRes.data?.data || []).filter(
 (type) => type.status !== "Inactive",
 );
 const savedRows =
 deductionsRes.data?.data?.deductions ||
 deductionsRes.data?.data ||
 deductionsRes.data?.deductions ||
 [];

 setDeductionTypes(activeTypes);
 setDeductionModal({
 show: true,
 loading: false,
 saving: false,
 workOrder: wo,
 originalIds: savedRows.map((row) => row._id).filter(Boolean),
 rows: savedRows.length
 ? savedRows.map((row) => ({
 _id: row._id || "",
 name: getDeductionRowTypeId(row),
 percentage: row.percentage ?? "",
 remarks: row.remarks || "",
 }))
 : [{ _id: "", name: "", percentage: "", remarks: "" }],
 });
 } catch (error) {
 setDeductionModal((prev) => ({
 ...prev,
 loading: false,
 }));
 swal(
 "Error",
 error.response?.data?.message || "Failed to load deductions.",
 "error",
 );
 }
 };

 const handleCloseDeduction = () => {
 if (deductionModal.saving) return;
 setDeductionModal({
 show: false,
 loading: false,
 saving: false,
 workOrder: null,
 originalIds: [],
 rows: [{ _id: "", name: "", percentage: "", remarks: "" }],
 });
 };

 const saveDeductions = async () => {
 const rows = getDeductionRows().map((row) => ({
 _id: row._id || "",
 name: getDeductionRowTypeId(row),
 percentage: row.percentage,
 remarks: row.remarks || "",
 }));

 const hasInvalidRow = rows.some(
 (row) =>
 !row.name ||
 row.percentage === "" ||
 Number(row.percentage) < 0 ||
 Number(row.percentage) > 100,
 );

 if (hasInvalidRow) {
 return swal(
 "Validation",
 "Please select deduction type and enter percentage between 0 and 100 for every row.",
 "warning",
 );
 }

 try {
 setDeductionModal((prev) => ({ ...prev, saving: true }));
 const currentIds = rows.map((row) => row._id).filter(Boolean);
 const deletedIds = deductionModal.originalIds.filter(
 (id) => !currentIds.includes(id),
 );

 await Promise.all([
 ...deletedIds.map((id) => api.delete(`/deductions/${id}`)),
 ...rows.map((row) => {
 const payload = {
 workOrder: deductionModal.workOrder.id,
 name: row.name,
 percentage: Number(row.percentage || 0),
 remarks: row.remarks,
 };

 return row._id
 ? api.patch(`/deductions/${row._id}`, payload)
 : api.post("/deductions", payload);
 }),
 ]);
 swal("Success", "Deductions saved successfully", "success");
 handleCloseDeduction();
 } catch (error) {
 swal(
 "Error",
 error.response?.data?.message || "Failed to save deductions.",
 "error",
 );
 } finally {
 setDeductionModal((prev) => ({ ...prev, saving: false }));
 }
 };

 const handleOpenDocuments = async (wo) => {
 try {
 setDocumentsModal({
 show: true,
 loading: true,
 workOrder: wo,
 documents: [],
 });

 const response = await api.get("/documents", {
 params: { workOrder: wo.id, limit: 1000 },
 });

 if (response.data.success) {
 const workOrderDocuments = (response.data.data || []).filter((doc) => {
 const typeName = normalizeTypeName(doc.documentType?.name);
 return typeName === "workorder";
 });

 setDocumentsModal({
 show: true,
 loading: false,
 workOrder: wo,
 documents: workOrderDocuments,
 });
 }
 } catch (error) {
 setDocumentsModal({
 show: true,
 loading: false,
 workOrder: wo,
 documents: [],
 });
 swal("Error", "Failed to load work order documents.", "error");
 }
 };

 const handleDeleteDocument = async (doc) => {
 const confirmed = await swal({
 title: "Delete document?",
 text: "This action cannot be undone.",
 icon: "warning",
 buttons: ["Cancel", "Delete"],
 dangerMode: true,
 });

 if (!confirmed) return;

 try {
 const response = await api.delete(`/documents/${doc._id}`);
 if (response.data.success) {
 setDocumentsModal((prev) => ({
 ...prev,
 documents: prev.documents.filter((item) => item._id !== doc._id),
 }));
 swal("Deleted!", "Document removed successfully.", "success");
 }
 } catch (error) {
 swal("Error", "Failed to delete document.", "error");
 }
 };

 const handleOpenEmail = (wo) => {
 setEmailInitialData({
 to: wo.clientEmail || "",
 clientId: wo.clientId || "",
 subject: `Regarding Work Order: ${wo.woNo || ""}`,
 placeholders: {
 clientName: wo.client || "",
 projectName: wo.project || "",
 number: wo.woNo || "",
 type: "Work Order",
 }
 });
 setShowEmailModal(true);
 };

 const handleDownloadDocument = (doc) => {
 const link = document.createElement("a");
 link.href = getFileUrl(doc.fileUrl);
 link.download = doc.fileName || "document";
 link.click();
 };

  if (viewMode === "cards") {
    return (
      <WorkOrderCards
        onViewModeChange={(mode) => {
          localStorage.setItem("workOrderViewMode", mode);
          setViewMode(mode);
        }}
      />
    );
  }

  return (
    <Fragment>
      <PageTitle activeMenu="Work Orders" motherMenu="Work Orders" />

      <Row className="mb-3 align-items-center">
        <Col xs={12} md={7} className="d-flex align-items-center flex-wrap gap-3 mb-2 mb-md-0">
          <h4 className="fw-bold mb-0">Work Orders</h4>

          {/* View Mode Toggle */}
          <div
            className="d-inline-flex align-items-center bg-white border rounded-3 p-1 shadow-sm"
            style={{ height: "40px", boxSizing: "border-box" }}
          >
            <button
              type="button"
              className="btn btn-light text-muted bg-transparent d-inline-flex align-items-center justify-content-center px-3 border-0"
              style={{
                height: "32px",
                fontSize: "0.85rem",
                fontWeight: 500,
                borderRadius: "6px",
                gap: "8px",
                lineHeight: 1,
              }}
              onClick={() => {
                localStorage.setItem("workOrderViewMode", "cards");
                setViewMode("cards");
              }}
            >
              <i className="fas fa-th-large" style={{ fontSize: "0.85rem" }}></i>
              <span>Cards View</span>
            </button>
            <button
              type="button"
              className="btn btn-primary text-white shadow-sm d-inline-flex align-items-center justify-content-center px-3 border-0 ms-1"
              style={{
                height: "32px",
                fontSize: "0.85rem",
                fontWeight: 500,
                borderRadius: "6px",
                gap: "8px",
                lineHeight: 1,
              }}
            >
              <i className="fas fa-list" style={{ fontSize: "0.85rem" }}></i>
              <span>Table View</span>
            </button>
          </div>
        </Col>

 <Col xs={12} md={5} className="text-md-end">
 <PermissionWrapper user={user} required={["create:workorders"]}>
 <Link
 to={projectId ? `/work-orders/add?project=${projectId}` : "/work-orders/add"}
 className="btn btn-primary shadow-sm"
 style={{ borderRadius: "8px", height: "40px", display: "inline-flex", alignItems: "center" }}
 >
 + Add Work Order
 </Link>
 </PermissionWrapper>
 </Col>
 </Row>

      {/* FILTER BAR - EXACT SAME AS CARD VIEW */}
      <Card className="shadow-sm border-0 mb-3">
        <Card.Body>
          <Row className="g-2">
            <Col xl={3} lg={6} md={6}>
              <Form.Control
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search WO No, Project, Client..."
              />
            </Col>
            <Col xl={2} lg={6} md={6}>
              <Form.Select name="client" value={filters.client} onChange={handleFilterChange}>
                <option value="">All Clients</option>
                {clientOptions.map((client) => (
                  <option key={client.value} value={client.value}>
                    {client.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xl={2} lg={6} md={6}>
              <Form.Select name="project" value={filters.project} onChange={handleFilterChange}>
                <option value="">All Projects</option>
                {projectOptions.map((proj) => (
                  <option key={proj.value} value={proj.value}>
                    {proj.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xl={2} lg={6} md={6}>
              <Form.Select name="status" value={filters.status} onChange={handleFilterChange}>
                <option value="">All Status</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </Form.Select>
            </Col>
            <Col xl={2} lg={6} md={6}>
              <Form.Select
                name="billingStatus"
                value={filters.billingStatus}
                onChange={handleFilterChange}
              >
                <option value="">Billing Status</option>
                <option value="Unbilled">Unbilled</option>
                <option value="Partially Billed">Partially Billed</option>
                <option value="Fully Billed">Fully Billed</option>
                <option value="Billed">Billed</option>
              </Form.Select>
            </Col>
            <Col xl={1} lg={12} md={12} className="d-grid">
              <Button variant="outline-secondary" onClick={clearFilters}>
                Reset
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row>
        <Col lg={12}>
 <Card className="shadow-sm border-0">
 <Card.Header>
 <Card.Title className="mb-0">Work Order List</Card.Title>
 </Card.Header>

 <Card.Body>
 <div className="table-responsive" style={{ maxHeight: "700px", overflowY: "auto" }}>
 <Table hover className="align-middle">
 <thead className="sticky-top bg-white">
 <tr>
 <th>#</th>
 <th>WO No</th>
 <th>Client</th>
 <th>Project</th>
 {hasBillingPerm && <th>Value</th>}
 <th>Status</th>
 <th>Start Date</th>
 <th>End Date</th>
 <th>Duration</th>
 <th>Location</th>
 <th>Actions</th>
 </tr>
 </thead>
 <tbody>
 {loading ? (
 <tr>
 <td colSpan={hasBillingPerm ? 11 : 10} className="text-center py-5">
 <div className="py-4">
 <Spinner animation="border" variant="primary" style={{ width: "2.5rem", height: "2.5rem" }} role="status" />
 <p className="text-muted fw-semibold mt-3 mb-0">Loading work orders...</p>
 </div>
 </td>
 </tr>
 ) : filteredWorkOrders.length === 0 ? (
  <tr>
    <td colSpan={hasBillingPerm ? 11 : 10} className="text-center py-5">
      <p className="text-muted mb-3">No work orders found matching your filters.</p>
      <Button variant="outline-secondary" size="sm" onClick={clearFilters} className="me-2">
        Reset Filters
      </Button>
      <Link to={projectId ? `/work-orders/add?project=${projectId}` : "/work-orders/add"} className="btn btn-primary btn-sm">
        + Add Work Order
      </Link>
    </td>
  </tr>
) : (
  currentTableItems.map((wo, i) => (
    <tr key={wo.id}>
      <td>
        {(currentPage - 1) * itemsPerPage + (i + 1)}
      </td>

  <td>
    <div>{wo.woNo}</div>
    {wo.label && <div className="text-muted small fw-medium">{wo.label}</div>}
  </td>
 <td>{wo.client}</td>
 <td>{wo.project}</td>
 {hasBillingPerm && (
 <td>
 <div>
 {/* GST TOTAL */}
 <strong>
 ₹{wo.value?.toLocaleString("en-IN")}
 </strong>

 {/* BASE VALUE (optional) */}
 {wo.baseValue && (
 <div className="text-muted small">
 Base: ₹{wo.baseValue.toLocaleString("en-IN")}
 </div>
 )}

 {/* VALUE TEXT */}
 {wo.valueText && (
 <div className="text-muted small">
 {wo.valueText}
 </div>
 )}
 </div>
 </td>
 )}

 <td>
 <Badge
 bg={
 wo.status === "In Progress"
 ? "warning"
 : wo.status === "Pending"
 ? "secondary"
 : wo.status === "Approved"
 ? "success"
 : wo.status === "Completed"
 ? "info"
 : "dark"
 }
 >
 {wo.status}
 </Badge>
 </td>

 <td>{wo.startDate}</td>
 <td>{wo.endDate}</td>
 <td>
 {wo.totalDays ? (
 <Badge bg="info">{wo.totalDays} days</Badge>
 ) : (
 "—"
 )}
 </td>

 {/* Only Location shown */}
 <td>{wo.location}</td>

 <td>
 <div
 className="d-grid gap-1"
 style={{ gridTemplateColumns: "repeat(4, 32px)" }}
 >
 <PermissionWrapper user={user} required={["read:workorders"]}>
 <Button size="sm" variant="outline-primary" className="px-0 w-100" title="View Details" onClick={() => handleView(wo)}>
 <i className="fas fa-eye"></i>
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["edit:workorders"]}>
 <Button size="sm" variant="outline-success" className="px-0 w-100" title="Edit Work Order" onClick={() => handleEdit(wo)}>
 <i className="fas fa-edit"></i>
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:workorders"]}>
 <Button
 size="sm"
 variant="outline-warning"
 className="px-0 w-100"
 title="View BOQ"
 onClick={() =>
 navigate(
 projectId
 ? `/work-orders/${wo.id}/boq?project=${projectId}`
 : `/work-orders/${wo.id}/boq`
 )
 }
 >
 <i className="fas fa-list"></i>
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["send:email"]}>
 <Button size="sm" variant="outline-info" className="px-0 w-100" title="Send Email" onClick={() => handleOpenEmail(wo)}>
 <i className="fas fa-envelope"></i>
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:documents"]}>
 <Button size="sm" variant="outline-secondary" className="px-0 w-100" title="Work Order Documents" onClick={() => handleOpenDocuments(wo)}>
 <i className="fas fa-folder-open"></i>
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["read:gantt"]}>
 <Button size="sm" variant="outline-info" className="px-0 w-100" title="View Gantt Chart" onClick={() => handleGantt(wo)}>
 <i className="fas fa-chart-bar"></i>
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["edit:workorders"]}>
 <Button size="sm" variant="outline-dark" className="px-0 w-100" title="Add Deduction" onClick={() => handleOpenDeduction(wo)}>
 <i className="fas fa-percent"></i>
 </Button>
 </PermissionWrapper>

 <PermissionWrapper user={user} required={["delete:workorders"]}>
 <Button
 size="sm"
 variant="outline-danger"
 className="px-0 w-100"
 title="Delete Work Order"
 onClick={() => setConfirmDelete({ show: true, wo })}
 >
 <i className="fas fa-trash"></i>
 </Button>
 </PermissionWrapper>
 </div>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </Table>
 </div>

        {!loading && filteredWorkOrders.length > 0 && (
          <div className="mt-3">
            <AppPagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={filteredWorkOrders.length}
              itemsLength={currentTableItems.length}
              limit={itemsPerPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        )}
 </Card.Body>
 </Card>
 </Col>
 </Row>

 {/* Confirm Delete Modal */}
 <Modal
 show={confirmDelete.show}
 onHide={() => setConfirmDelete({ show: false, wo: null })}
 >
 <Modal.Header closeButton>
 <Modal.Title>Delete Work Order</Modal.Title>
 </Modal.Header>

 <Modal.Body>
 Are you sure you want to delete work order{" "}
 <b>{confirmDelete.wo?.woNo}</b>?
 <p className="text-muted mt-2 mb-0">
 <small>ID: {confirmDelete.wo?.id}</small>
 </p>
 </Modal.Body>

 <Modal.Footer>
 <Button
 variant="secondary"
 onClick={() => setConfirmDelete({ show: false, wo: null })}
 >
 Cancel
 </Button>

 <Button variant="danger" onClick={confirmDeleteWO}>
 Delete
 </Button>
 </Modal.Footer>
 </Modal>

 <Modal
 show={documentsModal.show}
 onHide={() =>
 setDocumentsModal({
 show: false,
 loading: false,
 workOrder: null,
 documents: [],
 })
 }
 size="lg"
 >
 <Modal.Header closeButton>
 <Modal.Title>
 Work Order Documents
 {documentsModal.workOrder?.woNo
 ? ` - ${documentsModal.workOrder.woNo}`
 : ""}
 </Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {documentsModal.loading ? (
 <div className="text-center py-4">Loading documents...</div>
 ) : documentsModal.documents.length === 0 ? (
 <div className="text-center text-muted py-4">
 No work order type documents found.
 </div>
 ) : (
 <Table responsive bordered hover>
 <thead>
 <tr>
 <th>#</th>
 <th>Type</th>
 <th>File</th>
 <th>Date</th>
 <th>Actions</th>
 </tr>
 </thead>
 <tbody>
 {documentsModal.documents.map((doc, index) => (
 <tr key={doc._id}>
 <td>{index + 1}</td>
 <td>{doc.documentType?.name || "Work Order"}</td>
 <td>{doc.fileName}</td>
 <td>
 {doc.submissionDate
 ? new Date(doc.submissionDate).toLocaleDateString("en-IN")
 : "—"}
 </td>
 <td>
 <div className="d-flex gap-2">
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => window.open(getFileUrl(doc.fileUrl), "_blank")}
 >
 View
 </Button>
 <Button
 size="sm"
 variant="outline-success"
 onClick={() => handleDownloadDocument(doc)}
 >
 Download
 </Button>
 <Button
 size="sm"
 variant="outline-danger"
 onClick={() => handleDeleteDocument(doc)}
 >
 Delete
 </Button>
 </div>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 )}
 </Modal.Body>

 <Modal.Footer>
 <Button
 variant="secondary"
 onClick={() =>
 setDocumentsModal({
 show: false,
 loading: false,
 workOrder: null,
 documents: [],
 })
 }
 >
 Close
 </Button>
 </Modal.Footer>
 </Modal>

 {deductionModal.show && (
 <div className="modal fade show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
 <div className="modal-dialog modal-lg modal-dialog-centered">
 <div className="modal-content border-0">
 <div className="modal-header border-bottom">
 <h5 className="modal-title fw-bold">
 Add Deduction
 {deductionModal.workOrder?.woNo
 ? ` - ${deductionModal.workOrder.woNo}`
 : ""}
 </h5>
 <button
 type="button"
 className="btn-close"
 onClick={handleCloseDeduction}
 disabled={deductionModal.saving}
 ></button>
 </div>

 <div className="modal-body p-4">
 {deductionModal.loading ? (
 <div className="text-center py-4">
 <Spinner animation="border" />
 <div className="text-muted mt-2">Loading deductions...</div>
 </div>
 ) : (
 <>
 <div className="mb-4">
 <Form.Label className="fw-bold small text-muted text-uppercase">
 Work Order Value
 </Form.Label>
 <div className="fw-bold">
 Rs. {(deductionModal.workOrder?.value || 0).toLocaleString("en-IN")}
 </div>
 </div>

 <Table bordered hover responsive>
 <thead className="bg-light">
 <tr>
 <th className="fs-12">Deduction Type</th>
 <th className="fs-12 text-center" style={{ width: "150px" }}>
 Percentage (%)
 </th>
 <th className="fs-12">Remarks</th>
 <th style={{ width: "50px" }}></th>
 </tr>
 </thead>
 <tbody>
 {getDeductionRows().map((row, i) => (
 <tr key={i}>
 <td className="p-1">
 <Form.Select
 className="border-0 shadow-none"
 value={getDeductionRowTypeId(row)}
 onChange={(e) =>
 updateDeductionRow(i, "name", e.target.value)
 }
 disabled={deductionModal.saving}
 >
 <option value="">Select Deduction Type</option>
 {deductionTypes.map((type) => (
 <option key={type._id} value={type._id}>
 {type.name}
 </option>
 ))}
 </Form.Select>
 </td>
 <td className="p-1">
 <Form.Control
 type="number"
 min="0"
 step="0.01"
 className="border-0 shadow-none text-center fw-bold"
 value={row.percentage}
 onChange={(e) =>
 updateDeductionRow(i, "percentage", e.target.value)
 }
 disabled={deductionModal.saving}
 />
 </td>
 <td className="p-1">
 <Form.Control
 className="border-0 shadow-none"
 placeholder="Enter remarks..."
 value={row.remarks || ""}
 onChange={(e) =>
 updateDeductionRow(i, "remarks", e.target.value)
 }
 disabled={deductionModal.saving}
 />
 </td>
 <td className="text-center p-1">
 <Button
 variant="link"
 className="text-danger p-0"
 onClick={() => removeDeductionRow(i)}
 disabled={deductionModal.saving || getDeductionRows().length === 1}
 >
 <i className="fas fa-times"></i>
 </Button>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>

 <div className="d-flex justify-content-between align-items-center mt-3">
 <Button
 variant="link"
 className="text-primary p-0 fw-bold text-decoration-none"
 onClick={addDeductionRow}
 disabled={deductionModal.saving}
 >
 + Add Deduction
 </Button>
 <div className="text-end">
 <div className="fw-bold text-danger">
 Total Deduction: {totalDeductionPercentage}%
 </div>
 <div className="text-muted small">
 Amount: Rs. {totalDeductionAmount.toLocaleString("en-IN")}
 </div>
 </div>
 </div>
 </>
 )}
 </div>

 <div className="modal-footer bg-light border-top">
 <Button
 variant="secondary"
 onClick={handleCloseDeduction}
 disabled={deductionModal.saving}
 >
 Close
 </Button>
 <Button
 variant="primary"
 onClick={saveDeductions}
 disabled={deductionModal.loading || deductionModal.saving}
 >
 {deductionModal.saving ? "Saving..." : "Save Deduction"}
 </Button>
 </div>
 </div>
 </div>
 </div>
 )}

 <EmailModal
 show={showEmailModal}
 onHide={() => setShowEmailModal(false)}
 initialData={emailInitialData}
 />
 </Fragment>
 );
};

export default WorkOrders;
